import { motion } from "framer-motion";
import { Sprout, Dna, Microscope, ArrowUpRight } from "lucide-react";
import { ATHENA } from "@/constants/testIds";

const IMG_NUTRA = "/nutra-dna-beads.jpg";

const IMG_BIOTECH = "/biotech-molecular.jpg";

const IMG_LIFESCI = "/biotech-nucleus.jpg";

// Loop animations per icon — subtle, on-brand
const ICON_ANIMATIONS = {
  Sprout: {
    animate: { rotateY: [0, 360] },
    transition: { duration: 4.5, repeat: Infinity, ease: "easeInOut" },
  },
  Dna: {
    animate: { rotate: [0, 360] },
    transition: { duration: 14, repeat: Infinity, ease: "linear" },
  },
  Microscope: {
    animate: { y: [0, -10, 0, -6, 0], rotate: [0, -4, 0, 4, 0] },
    transition: { duration: 3, repeat: Infinity, ease: "easeInOut" },
  },
};

const DOMAINS = [
  {
    n: "01",
    code: "NUTRA",
    title: "Nutraceuticals",
    body:
      "Clinically-validated supplements, functional foods and personalised nutrition built around bio-active molecules — extracted, characterised and dose-optimised in-house.",
    bullets: ["Functional ingredients", "Custom formulations", "Bioavailability studies"],
    icon: Sprout,
    iconName: "Sprout",
    span: "lg:col-span-2 lg:row-span-2",
    testid: ATHENA.domainNutra,
    visual: "image",
    image: IMG_NUTRA,
    imageAlt: "Abstract DNA helix rendered as coloured particles",
    isFeatured: true,
  },
  {
    n: "02",
    code: "BIOTECH",
    title: "Biotechnology",
    body:
      "Recombinant proteins, fermentation-derived actives and precision-bioprocess scale-up for pharma and agri-bio partners.",
    bullets: ["Strain engineering", "Bioprocess scale-up", "Fermentation"],
    icon: Dna,
    iconName: "Dna",
    span: "lg:col-span-2",
    testid: ATHENA.domainBiotech,
    visual: "image",
    image: IMG_BIOTECH,
    imageAlt: "Abstract molecular cluster with glowing neon strands",
  },
  {
    n: "03",
    code: "LIFE SCI",
    title: "Life Sciences",
    body:
      "Translational research, contract analytics and clinical study support — from molecule to manuscript.",
    bullets: ["Pre-clinical assays", "Clinical study design", "Regulatory dossiers"],
    icon: Microscope,
    iconName: "Microscope",
    span: "lg:col-span-2",
    testid: ATHENA.domainLifeSciences,
    visual: "image",
    image: IMG_LIFESCI,
    imageAlt: "Abstract crystalline nucleus with radiating molecular strands",
  },
];

const Card = ({ d, index }) => {
  const Icon = d.icon;
  const isImage = d.visual === "image";
  const isDark = d.visual === "dark";
  const iconAnim = ICON_ANIMATIONS[d.iconName] || {};
  const iconColor = isImage ? "text-[#B89B64]" : isDark ? "text-[#B89B64]" : "text-[#1A382A]";

  return (
    <motion.article
      data-testid={d.testid}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, delay: index * 0.12 }}
      className={`group relative overflow-hidden border ${
        isDark
          ? "bg-[#0A1713] border-[#1A382A] text-[#F8F7F4]"
          : "bg-[#EAE7E0] border-[#D5D1C8] text-[#0A1713]"
      } ${d.span} p-7 md:p-8 transition-transform duration-500 hover:-translate-y-1`}
    >
      {/* background image variant */}
      {isImage && d.image && (
        <div className="absolute inset-0">
          <img
            src={d.image}
            alt={d.imageAlt || ""}
            className="w-full h-full object-cover opacity-60 transition-transform duration-[1500ms] ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#0A1713]/80 via-[#0A1713]/55 to-[#0A1713]/30" />
        </div>
      )}
      <div className={`relative z-10 flex flex-col h-full ${isImage ? "text-[#F8F7F4]" : ""}`}>
        <div className="flex items-start justify-between">
          <span className={`label-mono ${isImage ? "text-[#B89B64]" : ""}`}>
            {d.n} // {d.code}
          </span>
          <motion.div
            animate={iconAnim.animate}
            transition={iconAnim.transition}
            whileHover={{ scale: 1.25 }}
            className={`${iconColor} origin-center`}
          >
            <Icon
              size={d.isFeatured ? 36 : 26}
              strokeWidth={1.2}
            />
          </motion.div>
        </div>
        <h3 className={`font-display ${d.isFeatured ? "text-3xl md:text-4xl mt-8" : "text-2xl md:text-3xl mt-5"}`}>
          {d.title}
        </h3>
        <p
          className={`mt-3 max-w-md leading-relaxed ${d.isFeatured ? "text-sm md:text-base" : "text-xs md:text-sm"} ${
            isImage
              ? "text-[#F8F7F4]/90"
              : isDark
              ? "text-[#A2B3AB]"
              : "text-[#3D4F46]"
          }`}
        >
          {d.body}
        </p>
        {d.isFeatured && (
          <ul
            className={`mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs font-mono uppercase tracking-wider ${
              isImage ? "text-[#F8F7F4]/85" : isDark ? "text-[#A2B3AB]" : "text-[#3D4F46]"
            }`}
          >
            {d.bullets.map((b) => (
              <li key={b} className="flex items-center gap-2">
                <span
                  className={`w-1 h-1 rounded-full ${
                    isImage || isDark ? "bg-[#B89B64]" : "bg-[#1A382A]"
                  }`}
                />
                {b}
              </li>
            ))}
          </ul>
        )}
        <div className="flex-1" />
        <div
          className={`mt-5 flex items-center gap-2 text-[10px] md:text-xs font-mono uppercase tracking-widest ${
            isImage ? "text-[#B89B64]" : isDark ? "text-[#B89B64]" : "text-[#1A382A]"
          }`}
        >
          Capabilities <ArrowUpRight size={14} strokeWidth={1.4} />
        </div>
      </div>
    </motion.article>
  );
};

export const Domains = () => {
  return (
    <section
      id="domains"
      data-testid={ATHENA.domainsSection}
      className="relative pt-10 md:pt-14 pb-10 md:pb-14 border-t border-[#D5D1C8]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="label-mono">// DOMAINS OF EXPERTISE</span>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl mt-4 leading-[1.05] tracking-tight text-[#0A1713]">
              Three disciplines.
              <br />
              One integrated platform.
            </h2>
          </div>
          <p className="max-w-md text-[#3D4F46] leading-relaxed">
            We don&apos;t just outsource — we co-develop. Each project moves through
            our integrated workflow: discovery, characterisation, formulation,
            and clinical validation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 md:gap-4 auto-rows-[240px]">
          {DOMAINS.map((d, i) => (
            <Card key={d.code} d={d} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Domains;
